/*
## SiccasCartAjaxLoader (siccas-cart-drawer.js)

### Purpose
Enhances cart drawer functionality by dynamically loading product values and details
via AJAX when products are added to the cart.

### Related Files
- siccas-cart-drawer.js - Main file containing SiccasCartAjaxLoader class
- cart-drawer.js - Original Shopify cart drawer implementation that we enhance
- siccas-ajax-extraction.js - Helper utility for AJAX content management

### How It Works
1. Enhances the CartDrawer prototype from cart-drawer.js by extending its renderContents method
2. Detects empty containers for product values and details in the cart drawer
3. Loads content for each product from its respective product page
4. Integrates with SiccasAjaxExtraction to initialize interactive elements

### Key Components
- `SiccasCartAjaxLoader` - Main class that handles AJAX loading of product content
- `enhanceCartDrawer()` - Function that extends CartDrawer prototype
- `loadAllProductContent()` - Finds and loads content for all products in cart
- `loadProductContent()` - Loads content for a specific product

### Interaction with SiccasAjaxExtraction
- After loading product content, calls SiccasAjaxExtraction.reinitialize()
- This ensures any interactive elements in the loaded content are properly initialized
- SiccasAjaxExtraction handles popups and other interactive features in the loaded content

### Loading Process
1. When a product is added to cart, CartDrawer.renderContents() is called
2. Our enhanced version resets the loadedProductIds cache
3. Empty containers are identified (.siccas-cart__product-values:empty, .siccas-cart__product-detail:empty)
4. For each product, content is fetched from its product page
5. Content is extracted and inserted into the appropriate containers
6. SiccasAjaxExtraction is notified to initialize the new content

### Error Handling
- Safety timeout prevents the loading state from getting stuck
- Containers are marked with comments if content is not available
- Loading state is always reset even if errors occur
*/

class SiccasCartAjaxLoader {
  constructor() {
    this.cartDrawer = document.querySelector("cart-drawer");
    this.isLoading = false;
    this.loadingTimeout = null;
    this.loadedProductIds = new Set();
    this.productContentCache = new Map();
    this.observerPaused = false;
    this.debounceTimer = null;
    this.lastObservedTime = 0;
    this.requestsInProgress = 0;
    this.init();
  }

  init() {
    this.ajaxExtraction = window.SiccasAjaxExtraction
      ? window.SiccasAjaxExtraction.getInstance()
      : null;

    this.setupMutationObserver();
    this.setupCartChangeListener();
    this.checkAndLoadContent();
  }

  setupMutationObserver() {
    if (typeof MutationObserver !== "undefined" && this.cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        if (this.observerPaused || this.requestsInProgress > 5) return;

        const currentTime = Date.now();
        if (currentTime - this.lastObservedTime < 1000) return;
        this.lastObservedTime = currentTime;

        const relevantMutation = mutations.some((mutation) => {
          // Detect item removal
          if (mutation.type === "childList" && mutation.removedNodes.length) {
            for (const node of mutation.removedNodes) {
              if (
                node.nodeType === Node.ELEMENT_NODE &&
                node.classList?.contains("siccas-cart__item")
              ) {
                this.forceContentRefresh();
                return true;
              }
            }
          }

          // Skip non-childList mutations
          if (mutation.type !== "childList") return false;

          // Check for new empty containers
          return Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;

            if (
              node.classList &&
              (node.classList.contains("siccas-cart__product-values") ||
                node.classList.contains("siccas-cart__product-detail"))
            ) {
              return true;
            }

            return (
              node.querySelectorAll?.(
                ".siccas-cart__product-values:empty, .siccas-cart__product-detail:empty"
              ).length > 0
            );
          });
        });

        if (relevantMutation) this.debounceCheckAndLoadContent();
      });

      observer.observe(this.cartDrawer, {
        childList: true,
        subtree: true,
        characterData: false,
        attributeFilter: ["class"],
      });

      this.observer = observer;
    }
  }

  setupCartChangeListener() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function (url, options) {
      const responsePromise = originalFetch.apply(this, arguments);

      if (
        typeof url === "string" &&
        (url.includes("/cart/change") ||
          url.includes("/cart/update") ||
          url.includes("/cart/add"))
      ) {
        responsePromise
          .then(() => {
            setTimeout(() => self.forceContentRefresh(), 500);
          })
          .catch(() => {});
      }

      return responsePromise;
    };
  }

  forceContentRefresh() {
    this.loadedProductIds.clear();
    this.resetLoadingState();
    setTimeout(() => this.checkAndLoadContent(), 100);
  }

  debounceCheckAndLoadContent() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.checkAndLoadContent();
      this.debounceTimer = null;
    }, 300);
  }

  checkAndLoadContent() {
    if (this.isLoading || this.requestsInProgress > 5) return;

    const emptyValueContainers = document.querySelectorAll(
      ".siccas-cart__product-values:empty"
    );
    const emptyDetailContainers = document.querySelectorAll(
      ".siccas-cart__product-detail:empty"
    );

    // Only check containers that haven't been loaded
    const hasNewContainers = [
      ...emptyValueContainers,
      ...emptyDetailContainers,
    ].some((container) => {
      const productId = container.getAttribute("data-product-id");
      return productId && !this.loadedProductIds.has(productId);
    });

    if (hasNewContainers) this.loadAllProductContent();
  }

  loadAllProductContent() {
    this.resetLoadingState();
    this.observerPaused = true;

    const valueContainers = document.querySelectorAll(
      ".siccas-cart__product-values:empty"
    );
    const detailContainers = document.querySelectorAll(
      ".siccas-cart__product-detail:empty"
    );

    if (!valueContainers.length && !detailContainers.length) {
      this.observerPaused = false;
      return;
    }

    this.isLoading = true;
    this.loadingTimeout = setTimeout(() => {
      this.resetLoadingState();
      this.observerPaused = false;
    }, 5000);

    // Create a map of product IDs to their containers
    const productContainers = new Map();

    // Collect containers by product ID
    const collectContainers = (containers, type) => {
      Array.from(containers).forEach((container) => {
        const productId = container.getAttribute("data-product-id");
        if (!productId || this.loadedProductIds.has(productId)) return;

        if (!productContainers.has(productId)) {
          productContainers.set(productId, {
            valueContainer: null,
            detailContainer: null,
          });
        }

        productContainers.get(productId)[type] = container;
      });
    };

    collectContainers(valueContainers, "valueContainer");
    collectContainers(detailContainers, "detailContainer");

    // Load content for each product
    let loadedCount = 0;
    const totalProducts = productContainers.size;

    if (totalProducts === 0) {
      this.resetLoadingState();
      this.observerPaused = false;
      return;
    }

    const onComplete = () => {
      loadedCount++;
      if (loadedCount === totalProducts) {
        this.resetLoadingState();
        setTimeout(() => {
          this.observerPaused = false;
        }, 500);
        this.notifyContentLoaded();
      }
    };

    productContainers.forEach((containers, productId) => {
      this.loadProductContent(
        productId,
        containers.valueContainer,
        containers.detailContainer
      )
        .then(onComplete)
        .catch(onComplete);
    });
  }

  resetLoadingState() {
    this.isLoading = false;
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  async loadProductContent(productId, valueContainer, detailContainer) {
    if (this.loadedProductIds.has(productId)) return;
    this.requestsInProgress++;
    this.loadedProductIds.add(productId);

    try {
      const parentItem = (valueContainer || detailContainer)?.closest(
        ".siccas-cart__item"
      );
      if (!parentItem) {
        this.requestsInProgress--;
        return;
      }

      const productHandle = this.getProductHandleFromItem(parentItem);
      if (!productHandle) {
        this.requestsInProgress--;
        return;
      }

      // Check cache first
      let html = this.productContentCache.get(productHandle);

      if (!html) {
        html = await this.fetchHtml(`/products/${productHandle}`);
        if (html) this.productContentCache.set(productHandle, html);
      }

      if (!html) {
        this.requestsInProgress--;
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");

      if (valueContainer && valueContainer.innerHTML === "") {
        const productValues = doc.querySelector(".product-values");
        valueContainer.innerHTML = productValues
          ? productValues.outerHTML
          : "<!-- Product values not available -->";
      }

      if (detailContainer && detailContainer.innerHTML === "") {
        const productDetails = doc.querySelector(
          ".product__accordion.product__details"
        );
        detailContainer.innerHTML = productDetails
          ? productDetails.outerHTML
          : "<!-- Product details not available -->";
      }
    } catch (error) {
      console.error(
        `Error loading product content for ID ${productId}:`,
        error
      );

      if (valueContainer && valueContainer.innerHTML === "") {
        valueContainer.innerHTML = "<!-- Error loading product values -->";
      }
      if (detailContainer && detailContainer.innerHTML === "") {
        detailContainer.innerHTML = "<!-- Error loading product details -->";
      }
    } finally {
      this.requestsInProgress--;
    }
  }

  getProductHandleFromItem(cartItem) {
    const productLinks = cartItem.querySelectorAll('a[href*="/products/"]');
    if (!productLinks.length) return null;

    const href = productLinks[0].getAttribute("href");
    const matches = href.match(/\/products\/([^?#]+)/);
    return matches?.[1] || null;
  }

  async fetchHtml(url) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: { "Cache-Control": "max-age=3600" },
      });

      clearTimeout(timeoutId);

      if (!response.ok)
        throw new Error(`Network response was not ok: ${response.status}`);
      return await response.text();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(`Error fetching ${url}:`, error);
      }
      return null;
    }
  }

  notifyContentLoaded() {
    setTimeout(() => {
      if (this.ajaxExtraction) {
        this.ajaxExtraction.reinitialize();
      } else if (window.SiccasAjaxExtraction) {
        window.SiccasAjaxExtraction.getInstance().reinitialize();
      }
    }, 100);
  }
}

// Store the instance
let siccasCartAjaxLoaderInstance = null;

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // Make sure SiccasAjaxExtraction is initialized first
  if (window.SiccasAjaxExtraction) {
    window.SiccasAjaxExtraction.getInstance();
  }

  // Then initialize cart loader
  if (!siccasCartAjaxLoaderInstance) {
    siccasCartAjaxLoaderInstance = new SiccasCartAjaxLoader();
    window.siccasCartAjaxLoaderInstance = siccasCartAjaxLoaderInstance;
  }

  // Enhance CartDrawer after it's defined
  enhanceCartDrawer();
});

// Fallback initialization for some browsers
if (
  document.readyState === "complete" ||
  document.readyState === "interactive"
) {
  setTimeout(() => {
    if (window.SiccasAjaxExtraction) {
      window.SiccasAjaxExtraction.getInstance();
    }

    if (!siccasCartAjaxLoaderInstance) {
      siccasCartAjaxLoaderInstance = new SiccasCartAjaxLoader();
      window.siccasCartAjaxLoaderInstance = siccasCartAjaxLoaderInstance;
    }

    enhanceCartDrawer();
  }, 10);
}

function enhanceCartDrawer() {
  if (!customElements.get("cart-drawer")) {
    setTimeout(enhanceCartDrawer, 100);
    return;
  }

  const CartDrawerPrototype = customElements.get("cart-drawer").prototype;
  if (CartDrawerPrototype._siccasEnhanced) return;

  CartDrawerPrototype._siccasEnhanced = true;
  const originalRenderContents = CartDrawerPrototype.renderContents;

  CartDrawerPrototype.renderContents = function (parsedState) {
    originalRenderContents.call(this, parsedState);

    setTimeout(() => {
      if (window.siccasCartAjaxLoaderInstance) {
        window.siccasCartAjaxLoaderInstance.loadedProductIds.clear();
        window.siccasCartAjaxLoaderInstance.resetLoadingState();
        window.siccasCartAjaxLoaderInstance.loadAllProductContent();
      } else if (typeof SiccasCartAjaxLoader === "function") {
        window.siccasCartAjaxLoaderInstance = new SiccasCartAjaxLoader();
        window.siccasCartAjaxLoaderInstance.loadAllProductContent();
      }
    }, 300);
  };
}
